/**
 * EntityMediaHeader — Shared avatar + name + badges hero component
 *
 * DRY: Used in 3 places:
 * - EntityCard (card view, compact mode)
 * - EntityDetailPanel (drawer title)
 * - EntityInlineDetail (master-detail right panel)
 *
 * QR code popover via entityId prop (Phase 10).
 */

import {
  Group,
  Stack,
  Avatar,
  Text,
  Badge,
  ThemeIcon,
  ActionIcon,
  Popover,
} from '@mantine/core';
import { IconQrcode } from '@tabler/icons-react';
import { EntityQRCode } from './EntityQRCode';

export interface EntityMediaHeaderProps {
  name: string;
  subtitle?: string;
  status?: string;
  statusColorMap?: Record<string, string>;
  safetyLevel?: string;
  imageUrl?: string;
  accentColor?: string;
  Icon?: React.ComponentType<{ size: number }>;
  /** Smaller avatar for card view */
  compact?: boolean;
  /** Entity ID — enables QR button (Phase 10 wiring) */
  entityId?: string;
  /** Fullscreen badge hint */
  isFullscreen?: boolean;
  /** Max lines for subtitle (default 1). Pass 0 for no clamp. */
  subtitleLines?: number;
}

const SAFETY_COLORS: Record<string, string> = {
  CRITICAL: 'red',
  HIGH: 'orange',
  ELEVATED: 'yellow',
  STANDARD: 'gray',
  LOW: 'green',
};

function getInitials(name: string): string {
  return name
    .split(/\s+/)
    .map((word) => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

export function EntityMediaHeader({
  name,
  subtitle,
  status,
  statusColorMap,
  safetyLevel,
  imageUrl,
  accentColor = 'blue',
  Icon,
  compact = false,
  entityId,
  isFullscreen,
  subtitleLines = 1,
}: EntityMediaHeaderProps) {
  const avatarSize = compact ? 'md' : 48;

  return (
    <Group gap={compact ? 'xs' : 'sm'} wrap="nowrap" style={{ minWidth: 0 }}>
      <Avatar
        size={avatarSize}
        radius="xl"
        src={imageUrl ?? null}
        color={accentColor}
      >
        {!imageUrl && Icon ? (
          <Icon size={compact ? 16 : 20} />
        ) : !imageUrl ? (
          getInitials(name)
        ) : null}
      </Avatar>

      <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
        <Text fw={compact ? 600 : 700} size={compact ? 'sm' : 'md'} lineClamp={1}>
          {name}
        </Text>

        {subtitle && (
          <Text size="xs" c="dimmed" lineClamp={subtitleLines || undefined}>
            {subtitle}
          </Text>
        )}

        <Group gap="xs" wrap="nowrap">
          {status && (
            <Badge
              size="xs"
              variant="light"
              color={statusColorMap?.[status] ?? 'gray'}
            >
              {status}
            </Badge>
          )}

          {safetyLevel && safetyLevel !== 'STANDARD' && (
            <Badge
              size="xs"
              variant="light"
              color={SAFETY_COLORS[safetyLevel] ?? 'gray'}
            >
              {safetyLevel}
            </Badge>
          )}

          {isFullscreen && (
            <Badge size="xs" variant="light" color="blue">
              Esc to exit
            </Badge>
          )}

          {/* QR code popover — scan to open entity on mobile */}
          {entityId && !compact && (
            <Popover position="bottom" shadow="md" width={180}>
              <Popover.Target>
                <ActionIcon variant="subtle" size="xs" aria-label="Show QR code">
                  <IconQrcode size={12} />
                </ActionIcon>
              </Popover.Target>
              <Popover.Dropdown>
                <Stack gap="xs" align="center">
                  <Text size="xs" c="dimmed">Scan to open on mobile</Text>
                  <EntityQRCode
                    value={`${globalThis.location.origin}/entity/${entityId}`}
                    size={128}
                    entityName={name}
                  />
                </Stack>
              </Popover.Dropdown>
            </Popover>
          )}
        </Group>
      </Stack>
    </Group>
  );
}
